import { css } from '@emotion/react';
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { ExportReportType } from '~/exportReport/exportReportType';
import { ExportReportTemplateLoaderComponent } from '~/exportReport/templateLoader/exportReportTemplateLoader.component';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { MapExportReportPreviewLocationComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewLocation.component';
import type { ModalProps } from '~/modal/modalType.enum';
import { modalContentStyle } from '~/proximity/edit/proximityEdit.styles';
import { createReportCreateRequest } from '~/store/frontendState/createReport/createReport.actionCreators';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

export type MapExportReportPreviewModalModalProps = ModalProps;

const previewWrapperStyle = css({
  minWidth: 1000,
});

const settingsWrapperStyle = css({
  width: 1000,
  margin: '20px auto 0',
});

export const MapExportReportPreviewModalContainer: FC<MapExportReportPreviewModalModalProps> = ({ onClose, isOpen }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const exportReportState = useToolsStateExportReportSelector();
  const mapId = useMapIdSelector();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { tableColumns } = useExportReportParams();

  const { isLoading, getDemographicData, getDemographicLabel, isError } = useExportReportData({
    isPreviewMode,
    tableColumns,
    template: exportReportState.template,
  });

  const handleClose = useCallback(() => {
    dispatch(exportReportDisable());
    onClose();
  }, [dispatch, onClose]);

  const handlePrint = useCallback(() => {
    if (!mapId) {
      return;
    }

    dispatch(createReportCreateRequest(mapId));
    onClose();
  }, [mapId, dispatch, onClose]);

  if (!exportReportState.isActive) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      caption={t('Location Summary')}
      confirmButton={(
        <ButtonComponent
          prefixIcon={faPrint}
          text={t('Print Report')}
          onClick={handlePrint}
        />
      )}
      contentStyle={modalContentStyle}
      additionalContent={isLoading && <OverlayLoaderComponent />}
      maxWidth={1100}
    >
      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={previewWrapperStyle}>
          <div css={settingsWrapperStyle}>
            <ExportReportTemplateLoaderComponent />

            <CheckboxComponent
              checkedSetter={() => setIsPreviewMode(usePreview => !usePreview)}
              isChecked={isPreviewMode}
              text="Template Preview (no data fetch)"
            />
          </div>

          {exportReportState.params.type === ExportReportType.Location && (
            <MapExportReportPreviewLocationComponent
              params={exportReportState.params}
              template={exportReportState.template}
              tableColumns={tableColumns}
              isPreviewMode={isPreviewMode}
              isError={isError}
              getDemographicData={getDemographicData}
              getDemographicLabel={getDemographicLabel}
            />
          )}
        </div>
      </ScrollBarComponent>
    </ModalComponent>
  );
};
